import { useCallback, useRef, useState } from 'react';
import { Table as AntdTable } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import Cell from 'components/atoms/Cell';
import EditableCell from 'components/atoms/EditableCell';
import useTableBodyHeight from 'hooks/useTableBodyHeight';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'utils/pagination';
import Pagination from 'components/molecules/Pagination';
import ListView from './ListView';
import './index.scss';

const Table = ({
  columns,
  dataSource,
  updateDataSource,
  pagination = false,
  onChange,
  localePath = '',
  rowKey,
  scroll,
  footerExtra,
  ...props
}) => {
  // `common` carries the pager's own wording; the caller's namespace stays the
  // default one so column titles resolve exactly as they did.
  const { t } = useTranslation([localePath, 'common']);
  const isWide = useMedia('(min-width: 576px)');
  const containerRef = useRef(null);
  /* Tablets and desktops get a table that fills the rest of the viewport; the
     mobile breakpoint renders cards that are meant to flow with the page, and a
     caller passing its own `scroll` (a short summary table, say) opts out. */
  const autoHeight = isWide && !scroll;
  const bodyHeight = useTableBodyHeight(containerRef, autoHeight);

  /* A screen either drives its own paging - it passes `total`, having asked the
     API for a single page - or hands the whole array over and lets the table
     slice it. Page and size are tracked here either way, so an uncontrolled
     caller still gets a working pager and the two layouts agree on which page
     they are showing. */
  const paginated = pagination !== false && pagination != null;
  const [innerPage, setInnerPage] = useState({
    current: 1,
    pageSize: pagination.defaultPageSize || DEFAULT_PAGE_SIZE,
  });
  /* Picking a page size large enough to hold the whole list would otherwise
     take the pager off the screen along with the control that had just been
     used - leaving no way back to a smaller size. */
  const [sizeChosen, setSizeChosen] = useState(false);
  const current = pagination.current ?? innerPage.current;
  const pageSize = pagination.pageSize ?? innerPage.pageSize;
  const total = pagination.total ?? dataSource?.length ?? 0;
  const serverSide = pagination.total != null;

  const handlePageChange = useCallback(
    (next, ...rest) => {
      if (next.pageSize !== pageSize) setSizeChosen(true);
      setInnerPage({ current: next.current, pageSize: next.pageSize });
      if (onChange) onChange(next, ...rest);
    },
    [onChange, pageSize],
  );

  const paginationConfig = paginated && {
    // Small tables look as they always did: no pager until there is a second page.
    hideOnSinglePage: !sizeChosen,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    locale: { items_per_page: t('common:per-page') },
    size: isWide ? 'default' : 'small',
    ...pagination,
    current,
    pageSize,
    total,
  };

  /* A screen with something to say under its table - a "save your order" button,
     say - used to stack it below the pager, costing a whole row of the viewport
     that the table would otherwise have used for rows. Given `footerExtra`, both
     layouts drop their own pager and the two share one row instead. */
  const inlineFooter = footerExtra != null;

  const tableColumns = columns.map((col) => {
    const renderedCol = {
      render: (value = '', record) =>
        col.isEditable ? (
          <EditableCell column={col} record={record} updateRecord={updateDataSource} value={value} />
        ) : (
          <Cell column={col} value={value} record={record} t={t} />
        ),
      ...col,
      title: t(col.title),
      width: col.width,
    };
    return renderedCol;
  });

  /* Column widths are percentages, so a narrow screen used to squeeze a twelve
     column table until its titles were unreadable. Give the table a floor of
     ~110px per column and let it scroll sideways below that - a four column
     table still fits a tablet, a twelve column one scrolls. */
  const minWidth = Math.max(500, tableColumns.length * 110);

  return (
    <div
      className={inlineFooter ? 'custom-table custom-table--flush-footer' : 'custom-table'}
      ref={containerRef}
      /* antd only caps the body with `scroll.y`; the variable also gives it a
         floor so a half-empty table still runs to the bottom of the screen. */
      style={bodyHeight ? { '--table-body-height': `${bodyHeight}px` } : undefined}
    >
      {isWide ? (
        <AntdTable
          columns={tableColumns}
          dataSource={dataSource}
          bordered={true}
          pagination={inlineFooter ? false : paginationConfig}
          /* antd reports a page change through the table's own `onChange`, so
             the config above deliberately carries no handler of its own -
             holding both would fire the caller's refetch twice per click. */
          onChange={handlePageChange}
          scroll={scroll || { x: minWidth, y: bodyHeight ?? 500 }}
          rowKey={rowKey}
          {...props}
        />
      ) : (
        <ListView
          columns={tableColumns}
          dataSource={dataSource}
          pagination={paginationConfig}
          serverSide={serverSide}
          renderPagination={!inlineFooter}
          onPageChange={handlePageChange}
          localePath={localePath}
          rowKey={rowKey}
          {...props}
        />
      )}
      {inlineFooter && (
        <div className="custom-table__footer">
          {footerExtra}
          {paginated && (
            <Pagination
              current={current}
              pageSize={pageSize}
              total={total}
              showSizeChanger={paginationConfig.showSizeChanger}
              hideOnSinglePage={paginationConfig.hideOnSinglePage}
              onChange={handlePageChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.array,
  dataSource: PropTypes.array,
  updateDataSource: PropTypes.func,
  pagination: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  onChange: PropTypes.func,
  localePath: PropTypes.string,
  footerExtra: PropTypes.node,
};

export default Table;
